import React, { useState, useEffect } from 'react';
import { CreditCard, Loader, CheckCircle, Coins, Gift, Settings as SettingsIcon, IndianRupee } from 'lucide-react';
import {
  getBillingSettings, updateBillingSettings, getCreditBalance, createCreditOrder,
  verifyPayment, grantCredits, getPayments, getOrganizationsList,
} from '../services/api';

const role = (() => {
  try {
    const t = localStorage.getItem('admin_token');
    return JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).role || '';
  } catch { return ''; }
})();
const isSuper = role === 'super_admin';

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function Billing() {
  const [settings, setSettings] = useState(null);
  const [bal, setBal] = useState(null);
  const [payments, setPayments] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [credits, setCredits] = useState(10);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // super-admin forms
  const [price, setPrice] = useState('');
  const [grantOrg, setGrantOrg] = useState('');
  const [grantCr, setGrantCr] = useState(10);
  const [grantNote, setGrantNote] = useState('');

  const reload = async () => {
    try {
      const [s, p] = await Promise.all([getBillingSettings(), getPayments().catch(() => [])]);
      setSettings(s); setPrice(String(s.price_per_credit)); setPayments(p || []);
      if (isSuper) {
        const rawOrgs = (await getOrganizationsList().catch(() => [])) || [];
        setOrgs(rawOrgs.map((o) => ({ organisation_id: o.organization_id, name: o.organizationName })));
      }
      else setBal(await getCreditBalance().catch(() => null));
    } catch { setErr('Failed to load billing.'); }
  };
  useEffect(() => { reload(); }, []);

  const buy = async () => {
    setErr(''); setMsg(''); setBusy(true);
    try {
      const order = await createCreditOrder(Number(credits));
      if (!order.key_id) { setErr('Payments are not configured yet (Razorpay keys missing). Ask the platform admin.'); return; }
      const ok = await loadRazorpay();
      if (!ok) { setErr('Could not load the payment widget.'); return; }
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
        name: 'Interview', description: `${order.credits} interview credits`,
        handler: async (resp) => {
          try {
            await verifyPayment({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setMsg(`Payment successful — ${order.credits} credits added.`);
            reload();
          } catch (e) { setErr(e.response?.data?.detail || 'Payment verification failed.'); }
        },
      });
      rzp.open();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not start payment.');
    } finally { setBusy(false); }
  };

  const saveSettings = async () => {
    setErr(''); setMsg('');
    try { const s = await updateBillingSettings({ price_per_credit: Number(price) }); setSettings(s); setMsg('Pricing updated.'); }
    catch (e) { setErr(e.response?.data?.detail || 'Failed to update pricing.'); }
  };

  const doGrant = async () => {
    setErr(''); setMsg('');
    if (!grantOrg) { setErr('Choose an organization to grant credits to.'); return; }
    try {
      await grantCredits(grantOrg, Number(grantCr), grantNote);
      setMsg(`Granted ${grantCr} credits.`); setGrantNote('');
      const p = await getPayments().catch(() => []); setPayments(p || []);
    } catch (e) { setErr(e.response?.data?.detail || 'Grant failed.'); }
  };

  const inr = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN')}`;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-ink-100 flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-brand-400" /> Billing &amp; Credits
        </h2>
        <p className="text-sm text-ink-300 mt-1">
          One credit = one interview. {settings && <>Current price: <span className="text-ink-100 font-medium">₹{settings.price_per_credit}/credit</span>{!settings.razorpay_configured && <span className="text-amber-300"> · live payments not configured yet</span>}.</>}
        </p>
      </div>

      {err && <div className="text-sm text-red-400">{err}</div>}
      {msg && <div className="text-sm text-green-400 flex items-center gap-1.5"><CheckCircle className="w-4 h-4" /> {msg}</div>}

      {/* Org admin: balance + buy */}
      {!isSuper && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-ink-800 border border-ink-700 rounded-lg p-5">
            <p className="text-xs text-ink-400 uppercase tracking-wide flex items-center gap-1.5"><Coins className="w-4 h-4" /> Credits remaining</p>
            <p className="text-3xl font-bold text-ink-100 mt-1">{bal ? (bal.remaining === 'unlimited' ? 'Unlimited' : bal.remaining) : '—'}</p>
            {bal && bal.remaining !== 'unlimited' && <p className="text-xs text-ink-500 mt-1">{bal.used} used of {bal.quota}</p>}
          </div>
          <div className="bg-ink-800 border border-ink-700 rounded-lg p-5">
            <p className="text-xs text-ink-400 uppercase tracking-wide mb-2">Buy credits</p>
            <div className="flex items-center gap-2">
              <input type="number" min={1} value={credits} onChange={(e) => setCredits(e.target.value)}
                className="w-24 px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" />
              <span className="text-sm text-ink-400">credits {settings && `· ${inr(Number(credits) * settings.price_per_credit * 100)}`}</span>
            </div>
            {settings?.packs?.length > 0 && (
              <div className="flex gap-1.5 mt-2">
                {settings.packs.map((p) => (
                  <button key={p} onClick={() => setCredits(p)} className="px-2 py-1 text-xs rounded bg-ink-900 border border-ink-700 text-ink-300 hover:border-brand-500">{p}</button>
                ))}
              </div>
            )}
            <button onClick={buy} disabled={busy}
              className="mt-3 flex items-center gap-2 px-5 py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-400 disabled:opacity-50">
              {busy ? <Loader className="w-4 h-4 animate-spin" /> : <IndianRupee className="w-4 h-4" />} Buy credits
            </button>
          </div>
        </div>
      )}

      {/* Super admin: pricing + grant */}
      {isSuper && (
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-ink-800 border border-ink-700 rounded-lg p-5">
            <p className="text-sm font-semibold text-ink-100 mb-3 flex items-center gap-2"><SettingsIcon className="w-4 h-4 text-brand-400" /> Pricing</p>
            <label className="block text-xs text-ink-400 mb-1">Price per credit (INR)</label>
            <div className="flex gap-2">
              <input type="number" min={1} value={price} onChange={(e) => setPrice(e.target.value)}
                className="w-32 px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" />
              <button onClick={saveSettings} className="px-4 py-2 rounded-lg bg-brand-500 text-white text-sm hover:bg-brand-400">Save</button>
            </div>
          </div>
          <div className="bg-ink-800 border border-ink-700 rounded-lg p-5">
            <p className="text-sm font-semibold text-ink-100 mb-3 flex items-center gap-2"><Gift className="w-4 h-4 text-brand-400" /> Grant credits</p>
            <div className="space-y-2">
              <select value={grantOrg} onChange={(e) => setGrantOrg(e.target.value)} className="w-full px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100">
                <option value="">Select organization…</option>
                {orgs.filter((o) => o.organisation_id).map((o) => <option key={o.organisation_id} value={o.organisation_id}>{o.name}</option>)}
              </select>
              <div className="flex gap-2">
                <input type="number" min={1} value={grantCr} onChange={(e) => setGrantCr(e.target.value)} className="w-24 px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" placeholder="credits" />
                <input value={grantNote} onChange={(e) => setGrantNote(e.target.value)} className="flex-1 px-3 py-2 bg-ink-900 border border-ink-700 rounded-lg text-sm text-ink-100" placeholder="note (optional)" />
              </div>
              <button onClick={doGrant} className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700">Grant</button>
            </div>
          </div>
        </div>
      )}

      {/* Payments ledger */}
      <div>
        <h3 className="text-sm font-semibold text-ink-100 mb-3">Payment history</h3>
        {payments.length === 0 ? (
          <p className="text-ink-400 text-sm py-6 text-center bg-ink-800 border border-ink-700 rounded-lg">No payments yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-800">
              <thead className="bg-ink-800">
                <tr>
                  {isSuper && <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Org</th>}
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Credits</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-ink-400 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-ink-900 divide-y divide-ink-800">
                {payments.map((p) => (
                  <tr key={p.payment_id} className="hover:bg-ink-800">
                    {isSuper && <td className="px-4 py-3 text-sm text-ink-400">{p.organization_id}</td>}
                    <td className="px-4 py-3 text-sm text-ink-400">{p.created_at ? new Date(p.created_at).toLocaleString() : '—'}</td>
                    <td className="px-4 py-3 text-sm text-ink-300">{p.type === 'manual_grant' ? 'Grant' : 'Purchase'}</td>
                    <td className="px-4 py-3 text-sm text-ink-100">{p.credits}</td>
                    <td className="px-4 py-3 text-sm text-ink-400">{inr(p.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.status === 'paid' ? 'bg-green-500/15 text-green-300' : p.status === 'created' ? 'bg-amber-500/15 text-amber-300' : 'bg-red-500/15 text-red-300'
                      }`}>{p.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Billing;
